import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  FileType,
  GetInTouchData,
  HeroData,
  PublicBadgeDetailsParams,
  RichContentSectionData,
  SocialMediaData,
} from "../../../../../models";
import { getBadgeDetails } from "../../../../../service/publicquery";
import { Status } from "../../../../../service/Shared";
import { OverviewData } from "../models";

interface UseDetailsReturnData {
  dataIsLoading: boolean;
  badgeDetailsData: BadgeDetailsData | undefined;
}

export interface BadgeDetailsData {
  displayName: string | null;
  logo: string | null;
  websiteUrl: string | null;
  calculatedContactable: boolean;
  showProjects: boolean;
  socialMediaData: SocialMediaData;
  hero: HeroData;
  overview: OverviewData;
  getInTouch: GetInTouchData;
  ourServices: RichContentSectionData;
}

export const useDetails = (): UseDetailsReturnData => {
  const { badgeUuid } = useParams<PublicBadgeDetailsParams>();

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [badgeDetailsData, setBadgeDetailsData] = useState<BadgeDetailsData | undefined>(undefined);

  const fetchData = useCallback(async (): Promise<void> => {
    if (badgeUuid) {
      const res = await getBadgeDetails({
        badgeUuid,
      });

      if (res.status === Status.Success && res.data) {
        const logo = res.data.files.find((f) => f.type === FileType.LargeLogo)?.file.url || null;

        const heroValues: HeroData = {
          enabled: true,
          title: res.data.listing?.content?.hero?.title || null,
          subtitle: res.data.listing?.content?.hero?.subtitle || null,
          imageUrl: null,
        };

        const overviewValues: OverviewData = {
          enabled: res.data.listing?.content?.ourMission ? res.data.listing?.content?.ourMission?.enabled : false,
          content: res.data?.listing?.content?.ourMission?.content || null,
        };

        const getInTouchValues: GetInTouchData = {
          enabled: res.data.listing?.content?.getInTouch ? res.data.listing?.content?.getInTouch?.enabled : false,
        };

        const socialMediaValues: SocialMediaData = {
          twitterUsername: res.data.socialMediaTwitterUsername,
          linkedinURL: res.data.socialMediaLinkedInUrl,
          instagramUsername: res.data.socialMediaInstagramUsername,
          youtubeChannel: res.data.socialMediaYoutubeChannel,
          facebookURL: res.data.socialMediaFacebookUrl,
        };

        setBadgeDetailsData({
          displayName: res.data.displayName,
          logo,
          websiteUrl: res.data.websiteUrl,
          calculatedContactable: res.data.calculatedContactable,
          showProjects: res.data.listing?.content?.projects ? res.data.listing?.content?.projects?.enabled : false,
          socialMediaData: socialMediaValues,
          hero: heroValues,
          overview: overviewValues,
          getInTouch: getInTouchValues,
          ourServices: {
            enabled: res.data.listing?.content?.ourServices ? res.data.listing?.content?.ourServices?.enabled : false,
            part1: res.data.listing?.content?.ourServices?.part1
              ? {
                  title: res.data.listing?.content?.ourServices?.part1?.title,
                  content: res.data.listing?.content?.ourServices?.part1?.content,
                  imageUrl:
                    res.data.listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part1?.imageFileUuid
                    )?.file.url || null,
                  externalUrl: res.data.listing?.content?.ourServices?.part1?.externalUrl,
                }
              : null,
            part2: res.data.listing?.content?.ourServices?.part2
              ? {
                  title: res.data.listing?.content?.ourServices?.part2?.title,
                  content: res.data.listing?.content?.ourServices?.part2?.content,
                  imageUrl:
                    res.data.listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part2?.imageFileUuid
                    )?.file.url || null,
                  externalUrl: res.data.listing?.content?.ourServices?.part2?.externalUrl,
                }
              : null,
            part3: res.data.listing?.content?.ourServices?.part3
              ? {
                  title: res.data.listing?.content?.ourServices?.part3?.title,
                  content: res.data.listing?.content?.ourServices?.part3?.content,
                  imageUrl:
                    res.data.listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part3?.imageFileUuid
                    )?.file.url || null,
                  externalUrl: res.data.listing?.content?.ourServices?.part3?.externalUrl,
                }
              : null,
            part4: res.data.listing?.content?.ourServices?.part4
              ? {
                  title: res.data.listing?.content?.ourServices?.part4?.title,
                  content: res.data.listing?.content?.ourServices?.part4?.content,
                  imageUrl:
                    res.data.listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part4?.imageFileUuid
                    )?.file.url || null,
                  externalUrl: res.data.listing?.content?.ourServices?.part4?.externalUrl,
                }
              : null,
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchData().then(() => setDataIsLoading(false));
  }, [fetchData]);

  return {
    dataIsLoading,
    badgeDetailsData,
  };
};
